// frontend/src/pages/Login.jsx
//
// Two-pane login:
//   • Left  — a 2×2 editorial collage of real photography (office team,
//             property, retail, a closed deal) under a brand-color gradient,
//             standing in for "built for every industry," not just IT
//             hardware. Compressed into a short hero band on mobile instead
//             of disappearing entirely.
//   • Right — clean MUI sign-in form.
// Brand palette: navy #0b1d3a → teal #0e7490 (same as the rest of the app).
// Sized to fit a typical laptop viewport without scrolling — see the
// deliberately tight spacing on the form card in particular.

import React, { useState } from 'react';
import {
  Paper, TextField, Button, Typography, Box, Alert, CircularProgress,
  InputAdornment, IconButton, Chip, Link as MuiLink
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  Visibility, VisibilityOff, Lock as LockIcon, Email as EmailIcon, CheckCircle as CheckIcon,
  Apartment as ApartmentIcon, Storefront as StorefrontIcon, Shield as ShieldIcon,
  Factory as FactoryIcon, Hotel as HotelIcon, BusinessCenter as BusinessCenterIcon
} from '@mui/icons-material';

// Real photography, not stock-icon abstractions — see web/public/images/login.
// Downloaded once from Unsplash rather than hotlinked, so the page doesn't
// depend on Unsplash's CDN at runtime.
const COLLAGE = [
  { src: '/images/login/team.jpg',       alt: '' },
  { src: '/images/login/realestate.jpg', alt: '' },
  { src: '/images/login/retail.jpg',     alt: '' },
  { src: '/images/login/handshake.jpg',  alt: '' },
];

const INDUSTRIES = [
  { label: 'Real Estate',            Icon: ApartmentIcon },
  { label: 'Retail',                 Icon: StorefrontIcon },
  { label: 'Insurance',              Icon: ShieldIcon },
  { label: 'Manufacturing',          Icon: FactoryIcon },
  { label: 'Hospitality',            Icon: HotelIcon },
  { label: 'Professional Services',  Icon: BusinessCenterIcon },
];

// What the product actually does, not filler copy — every line here maps to
// a real, shipped capability (lead→quotation conversion, PO/vendor tracking,
// the profit-summary feature, the plan/industry-agnostic branding work).
const FEATURES = [
  'From first lead to signed quotation — one connected pipeline',
  'Real margin visibility: vendor cost vs. invoice, on every deal',
  'Multi-vendor purchase orders, tracked automatically',
  'Fully rebrandable — your logo, your colors, your industry',
];

const BRAND_GRADIENT = 'linear-gradient(135deg, #0b1d3a 0%, #1e3a8a 45%, #0e7490 100%)';

// 2×2 photo grid filling the whole panel, with a brand-color gradient laid
// over it for contrast — shared by the desktop side panel and the mobile
// hero band so they read as the same identity at two different sizes.
const PhotoCollage = ({ children, sx = {}, contentSx = {} }) => (
  <Box sx={{ position: 'relative', overflow: 'hidden', color: '#fff', ...sx }}>
    <Box
      aria-hidden="true"
      sx={{
        position: 'absolute', inset: 0,
        display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr'
      }}
    >
      {COLLAGE.map((img, i) => (
        <Box key={i} sx={{ position: 'relative', overflow: 'hidden' }}>
          <Box
            component="img"
            src={img.src}
            alt={img.alt}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        </Box>
      ))}
    </Box>
    <Box
      aria-hidden="true"
      sx={{
        position: 'absolute', inset: 0,
        background: `${BRAND_GRADIENT}`,
        opacity: 0.86
      }}
    />
    {/* Absolutely-positioned siblings above don't participate in flex layout,
        so the column/space-between that spreads the logo/headline/chips has
        to live on THIS wrapper, not the outer Box — otherwise every child
        just stacks at the top with dead space below. */}
    <Box sx={{
      position: 'relative', zIndex: 1, height: '100%',
      display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
      ...contentSx
    }}>
      {children}
    </Box>
  </Box>
);

// No card/plate at all — any box (solid or glass) reads as a sticker sitting
// on top of the photo rather than a logo that belongs to the scene. Instead,
// a stack of drop-shadows traces the artwork's own silhouette: a tight
// near-white rim plus a soft halo lift the dark-navy strokes off the dark
// panel, and a low dark shadow grounds it over the lighter photo patches —
// no rectangle ever appears, only the shape of the mark itself.
const LOGO_MERGE_FILTER =
  'drop-shadow(0 0 1.5px rgba(255,255,255,0.95)) ' +
  'drop-shadow(0 0 12px rgba(255,255,255,0.5)) ' +
  'drop-shadow(0 6px 16px rgba(0,0,0,0.35))';

// Soft blurred colour blob for depth behind the logo — purely decorative,
// absolutely positioned so it doesn't affect the flex flow of its siblings.
const GlowBlob = ({ top, left, size, color }) => (
  <Box aria-hidden="true" sx={{
    position: 'absolute', top, left, width: size, height: size,
    borderRadius: '50%', pointerEvents: 'none',
    background: `radial-gradient(circle, ${color}, transparent 72%)`,
    filter: 'blur(6px)'
  }} />
);

// ---------- desktop left pane ----------
const BrandPanel = () => (
  <PhotoCollage
    sx={{ flex: 1.15, display: { xs: 'none', md: 'flex' } }}
    contentSx={{ p: { md: 4.5, lg: 5.5 } }}
  >
    <GlowBlob top={-70} left="76%" size={260} color="rgba(96,165,250,0.45)" />
    <GlowBlob top="38%" left="72%" size={260} color="rgba(74,222,128,0.28)" />

    {/* Icon + wordmark only — the tagline is its own crisp text line below,
        not the tiny illegible fine print baked into the master logo file. */}
    <Box>
      <Box
        component="img" src="/images/brand/narevek-crm-logo-mark.png" alt="Narevek CRM"
        sx={{ width: 200, display: 'block', filter: LOGO_MERGE_FILTER }}
      />
      <Typography
        variant="body2"
        sx={{ mt: 1, opacity: 0.88, fontStyle: 'italic', letterSpacing: 0.3, textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}
      >
        Manage Relationships. Drive Growth.
      </Typography>
    </Box>

    {/* Middle — sharper headline + a feature list instead of one dense
        paragraph, closer to how established SaaS product pages read */}
    <Box>
      <Typography
        variant="h4"
        sx={{ fontWeight: 800, lineHeight: 1.18, mb: 0.75, textShadow: '0 4px 16px rgba(0,0,0,0.35)' }}
      >
        Every deal,<br />
        <Box component="span" sx={{ color: '#60a5fa' }}>end-to-end.</Box>
      </Typography>
      <Typography variant="body2" sx={{ opacity: 0.85, mb: 2.25, maxWidth: 440 }}>
        One CRM for every industry — from first enquiry to final invoice.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.1 }}>
        {FEATURES.map((line) => (
          <Box key={line} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.1 }}>
            <CheckIcon sx={{ fontSize: 19, mt: '1px', color: '#4ade80', flexShrink: 0 }} />
            <Typography variant="body2" sx={{ opacity: 0.94, lineHeight: 1.45 }}>
              {line}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>

    {/* Bottom — industries served, not product categories */}
    <Box>
      <Typography variant="caption" sx={{ opacity: 0.7, letterSpacing: 0.5, display: 'block', mb: 1 }}>
        BUILT FOR
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
        {INDUSTRIES.map(({ label, Icon }) => (
          <Chip
            key={label}
            label={label}
            size="small"
            icon={<Icon sx={{ fontSize: '15px !important', color: '#a7f3d0 !important' }} />}
            sx={{
              color: '#fff', fontWeight: 600, fontSize: '0.72rem',
              background: 'rgba(255,255,255,0.12)',
              backdropFilter: 'blur(4px)',
              border: '1px solid rgba(255,255,255,0.22)'
            }}
          />
        ))}
      </Box>
    </Box>
  </PhotoCollage>
);

// ---------- mobile hero band (shown instead of the side panel on small screens) ----------
const MobileHero = () => (
  <PhotoCollage
    sx={{ display: { xs: 'flex', md: 'none' }, height: 188, flexShrink: 0 }}
    contentSx={{ px: 3, py: 2.25 }}
  >
    <GlowBlob top={-50} left="72%" size={160} color="rgba(96,165,250,0.45)" />
    <Box
      component="img" src="/images/brand/narevek-crm-logo-mark.png" alt="Narevek CRM"
      sx={{ width: 130, display: 'block', filter: LOGO_MERGE_FILTER }}
    />
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.2, textShadow: '0 2px 10px rgba(0,0,0,0.35)' }}>
        Every deal, end-to-end.
      </Typography>
      <Typography variant="caption" sx={{ opacity: 0.9 }}>
        Built for every industry — not just one.
      </Typography>
    </Box>
  </PhotoCollage>
);

// ---------- right pane: sign-in form ----------
const FormPanel = ({ email, setEmail, password, setPassword, showPwd, setShowPwd, error, loading, onSubmit }) => (
  <Box
    sx={{
      flex: 1,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      p: { xs: 2.5, sm: 4 },
      background: '#f8fafc'
    }}
  >
    <Paper
      elevation={0}
      sx={{
        width: '100%', maxWidth: 400,
        p: { xs: 3, sm: 4 },
        borderRadius: 3,
        background: '#ffffff',
        boxShadow: '0 12px 40px rgba(15, 23, 42, 0.08)'
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
        <Box component="img" src="/images/brand/narevek-crm-logo-mark.png" alt="Narevek CRM" sx={{ width: 148 }} />
      </Box>
      <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', textAlign: 'center' }}>
        Welcome back
      </Typography>
      <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5, mb: 2.5, textAlign: 'center' }}>
        Sign in to continue to your dashboard.
      </Typography>

      <Box component="form" onSubmit={onSubmit} noValidate>
        <TextField
          margin="dense" required fullWidth autoFocus size="small"
          id="email" label="Email Address" name="email" autoComplete="email"
          value={email} onChange={(e) => setEmail(e.target.value)} disabled={loading}
          InputProps={{
            startAdornment: <InputAdornment position="start"><EmailIcon fontSize="small" color="action" /></InputAdornment>
          }}
        />
        <TextField
          margin="dense" required fullWidth size="small"
          name="password" label="Password" id="password" autoComplete="current-password"
          type={showPwd ? 'text' : 'password'}
          value={password} onChange={(e) => setPassword(e.target.value)} disabled={loading}
          InputProps={{
            startAdornment: <InputAdornment position="start"><LockIcon fontSize="small" color="action" /></InputAdornment>,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPwd(s => !s)} edge="end" size="small">
                  {showPwd ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />

        <Typography variant="body2" sx={{ textAlign: 'right', mt: 0.5 }}>
          <MuiLink component={RouterLink} to="/forgot-password">Forgot password?</MuiLink>
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>
        )}

        <Button
          type="submit" fullWidth variant="contained" size="large"
          disabled={loading}
          sx={{
            mt: 2, mb: 1.5, py: 1.2,
            fontWeight: 700, fontSize: 16,
            textTransform: 'none',
            background: 'linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 100%)',
            boxShadow: '0 8px 22px rgba(29, 78, 216, 0.32)',
            '&:hover': {
              background: 'linear-gradient(135deg, #1e40af 0%, #2563eb 100%)'
            }
          }}
        >
          {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Sign In'}
        </Button>

        <Typography variant="body2" sx={{ textAlign: 'center' }}>
          New company? <MuiLink component={RouterLink} to="/signup">Create your workspace</MuiLink>
        </Typography>

        <Box sx={{
          mt: 2, pt: 1.5, borderTop: '1px solid #e2e8f0',
          textAlign: 'center'
        }}>
          <Typography variant="caption" sx={{ color: '#cbd5e1' }}>
            © {new Date().getFullYear()} Narevek. All rights reserved.
          </Typography>
        </Box>
      </Box>
    </Paper>
  </Box>
);

// ---------- main ----------
export default function Login({ onLogin }) {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd]   = useState(false);
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const result = await onLogin(email, password);
    if (!result.success) setError(result.message);
    setLoading(false);
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, overflow: { xs: 'auto', md: 'hidden' } }}>
      <BrandPanel />
      <MobileHero />
      <FormPanel
        email={email} setEmail={setEmail}
        password={password} setPassword={setPassword}
        showPwd={showPwd} setShowPwd={setShowPwd}
        error={error} loading={loading}
        onSubmit={handleSubmit}
      />
    </Box>
  );
}
